import { defineStore } from "pinia";
import { sdk } from "@/js/sdk";
import { DataDrive, DataSourceTypeE, isLocalMountConfig, LocalMountConfig } from "@/types/api/dataSources";
import { useUserStore } from "./user";
import * as util from "@/js/utilities";
import { monitoringCaptureError } from "@/plugins/monitoring";
import { RemoteAccessState } from "@/types/general";
const fallbackDefaultMountConfig = null;

type StoreState = {
	driveList: string[];
	driveConfigs: Record<string, DataDrive>;
	bucketList: string[];
	gatewayList: string[];
	defaultMountConfig: undefined | null | LocalMountConfig;
	fallbackDefaultMountConfig: undefined | null | LocalMountConfig;
	gatewayFetchStatus: RemoteAccessState;
	driveFetchStatus: RemoteAccessState;
	bucketFetchStatus: RemoteAccessState;
};

export const useDataSourcesStore = defineStore("dataSources", {
	state: (): StoreState => ({
		driveList: [],
		driveConfigs: {},
		bucketList: [],
		gatewayList: [],
		defaultMountConfig: undefined,
		fallbackDefaultMountConfig: undefined,
		gatewayFetchStatus: { loading: false, error: false, message: "" },
		driveFetchStatus: { loading: false, error: false, message: "" },
		bucketFetchStatus: { loading: false, error: false, message: "" },
	}),
	getters: {
		getDefaultMountConfig: (state) => {
			//! Deprecated
			if (isLocalMountConfig(state.defaultMountConfig)) {
				return state.defaultMountConfig;
			} else {
				return fallbackDefaultMountConfig;
			}
		},
		loadError(state): boolean {
			return state.gatewayFetchStatus.error || state.driveFetchStatus.error || state.bucketFetchStatus.error;
		},
	},
	actions: {
		setDriveList(driveIDList: string[]) {
			this.driveList = driveIDList;
		},
		setBucketList(bucketIDList: string[]) {
			this.bucketList = bucketIDList;
		},
		setGatewayList(gatewayIDList: string[]) {
			this.gatewayList = gatewayIDList;
		},
		async fetchGatewayList() {
			this.gatewayFetchStatus.loading = true;
			try {
				const resp = await sdk.things.list();
				if (resp.ThingIDs) {
					this.setGatewayList(resp.ThingIDs);
				}
				this.gatewayFetchStatus.error = false;
			} catch (error) {
				this.gatewayFetchStatus.error = true;
				monitoringCaptureError(error, "Fetch user gateways list");
			} finally {
				this.gatewayFetchStatus.loading = false;
			}
		},
		async fetchDriveList() {
			this.driveFetchStatus.loading = true;
			try {
				const resp = await sdk.drives.list();
				if (resp.drive_ids) {
					this.setDriveList(resp.drive_ids);
				}
				this.driveFetchStatus.error = false;
			} catch (error) {
				this.driveFetchStatus.error = true;
				monitoringCaptureError(error, "Fetch user drives list");
			} finally {
				this.driveFetchStatus.loading = false;
			}
		},
		async fetchBucketList() {
			this.bucketFetchStatus.loading = true;
			try {
				const resp = await sdk.buckets.list();
				if (resp.bucket_ids) {
					this.setBucketList(resp.bucket_ids);
				}
				this.bucketFetchStatus.error = false;
			} catch (error) {
				this.bucketFetchStatus.error = true;
				monitoringCaptureError(error, "Fetch user buckets list");
			} finally {
				this.bucketFetchStatus.loading = false;
			}
		},
		async fetchAllDataSources() {
			await Promise.all([this.fetchGatewayList(), this.fetchDriveList(), this.fetchBucketList()]);
		},
		setDriveConfig(driveConfig: DataDrive) {
			this.driveConfigs[driveConfig.DriveID] = driveConfig;
		},
		async findDrivesMountingResource(resourceID: string): Promise<{ drives: DataDrive[]; allChecked: boolean }> {
			let allChecked = true;
			if (this.driveList.length === 0) {
				try {
					const resp = await sdk.drives.list();
					this.setDriveList(resp.drive_ids ?? []);
				} catch (error) {
					monitoringCaptureError(error, "Fetch user drives list");
					return { drives: [], allChecked: false };
				}
			}
			const driveIDs = this.driveList;
			const missingIDs = driveIDs.filter((id) => !this.driveConfigs[id]);
			if (missingIDs.length > 0) {
				const results = await Promise.allSettled(
					missingIDs.map((id) => sdk.drives.getOne(id).then((r) => r as unknown as DataDrive)),
				);
				results.forEach((r) => {
					if (r.status === "fulfilled") {
						this.setDriveConfig(r.value);
					}
				});
				if (results.some((r) => r.status === "rejected")) {
					allChecked = false;
				}
			}
			const drives = driveIDs
				.map((id) => this.driveConfigs[id])
				.filter(
					(drive): drive is DataDrive =>
						!!drive &&
						Array.isArray(drive.DataSources) &&
						drive.DataSources.some((source) => source.ResourceID === resourceID),
				);
			return { drives, allChecked };
		},
		async initialiseDataSourcesFromRemote() {
			this.fetchAllDataSources();
			this.getFallbackMountConfig();
			this.getGlobalDefaultMountConfig();
		},

		async isValidMountConfig(mountConfig: undefined | null | LocalMountConfig): Promise<boolean> {
			if (mountConfig === null) {
				return true;
			}

			if (mountConfig && isLocalMountConfig(mountConfig)) {
				let resourceIDValid: boolean = true;

				if (mountConfig.ResourceType === DataSourceTypeE.SignaloidCloudStorage) {
					const authUserStore = useUserStore();
					const userID = await authUserStore.getCurrentUserID();
					resourceIDValid = mountConfig.ResourceID === `signaloid-cloud-storage:/${userID}` ? true : false;
				}
				return resourceIDValid;
			}
			return false;
		},
		async validateOrGetValidMountConfig(
			mountConfig: undefined | null | LocalMountConfig,
		): Promise<null | LocalMountConfig> {
			if (mountConfig === null) {
				return null;
			}

			if (mountConfig && isLocalMountConfig(mountConfig)) {
				const isValidMountConfig = await this.isValidMountConfig(mountConfig);
				if (isValidMountConfig) {
					return mountConfig;
				}
			}

			// The input mount config is undefined...

			// Try to return global default
			const globalDefaultMountConfig = await this.getGlobalDefaultMountConfig();

			if (globalDefaultMountConfig) {
				return globalDefaultMountConfig;
			}

			// Try to return fallback
			const fallbackMountConfig = await this.getFallbackMountConfig();
			if (fallbackMountConfig) {
				return fallbackMountConfig;
			}

			// if all else fails return null
			return null;
		},

		async setDefaultMountConfig(mountConfig: null | LocalMountConfig, syncWithRemote: boolean) {
			if (isLocalMountConfig(mountConfig)) {
				// valid mount config
				this.defaultMountConfig = mountConfig;
			} else if (mountConfig === null) {
				// user has removed mounting
				this.defaultMountConfig = null;
			} else {
				// unknown mount config, set to default
				this.defaultMountConfig = await this.getFallbackMountConfig();
			}

			if (syncWithRemote) {
				const authUserStore = useUserStore();
				await authUserStore.syncUserPreferenceWithRemote({
					Execution_DefaultDataSources: JSON.stringify(mountConfig),
				});
			}
		},
		resetDefaultMountConfig() {
			this.setDefaultMountConfig(this.fallbackDefaultMountConfig ?? null, true);
		},
		removeDefaultMountConfig() {
			this.setDefaultMountConfig(null, true);
		},
		async getGlobalDefaultMountConfig() {
			if (this.defaultMountConfig !== undefined) {
				// i.e., the mount config is set or unset (=null)
				return this.defaultMountConfig;
			}
			try {
				const authUserStore = useUserStore();
				const preferencesFromDB = await authUserStore.getCurrentUserPreferences();
				const defaultMountConfigFromDB = preferencesFromDB?.Execution_DefaultDataSources;

				if (defaultMountConfigFromDB && typeof defaultMountConfigFromDB === "string") {
					// try to parse the string
					const parsedMountConfig = util.parseMountConfigString(defaultMountConfigFromDB);
					const isValidMountConfig = await this.isValidMountConfig(parsedMountConfig);
					if (isValidMountConfig) {
						// @ts-ignore
						this.setDefaultMountConfig(parsedMountConfig, false);
						return this.defaultMountConfig;
					} else {
						console.warn("Default mount config in DB is invalid. Resetting to fallback mount config.");
						const validMountConfig = await this.getFallbackMountConfig();
						this.setDefaultMountConfig(validMountConfig, true);
						return this.defaultMountConfig;
					}
				} else {
					// no data source set in DB, set to default mount config
					console.warn("No default mount config set in DB. Resetting to fallback mount config.");
					const validMountConfig = await this.getFallbackMountConfig();
					this.setDefaultMountConfig(validMountConfig, true);
					return this.defaultMountConfig;
				}
			} catch (error) {
				monitoringCaptureError(error, "Get user default mount config");
				return null;
			}
		},

		async getFallbackMountConfig() {
			/*
			 * this function should be called if there is no default set and
			 * you need to get a mount config to fallback onto. It will return
			 * a mount config for the cloud storage if possible, if not it will return `null`
			 */
			if (this.fallbackDefaultMountConfig !== undefined) {
				// i.e., the mount config is set or unset (=null)
				return this.fallbackDefaultMountConfig;
			}
			try {
				const authUserStore = useUserStore();
				const currentUserID = await authUserStore.getCurrentUserID();

				if (!currentUserID) {
					throw new Error("User ID invalid");
				}
				const fallbackMountConfig: LocalMountConfig = {
					Location: "sd0",
					ResourceID: `signaloid-cloud-storage:/${currentUserID}`,
					ResourceType: DataSourceTypeE.SignaloidCloudStorage,
				};

				this.fallbackDefaultMountConfig = fallbackMountConfig;
				return fallbackMountConfig;
			} catch (error) {
				monitoringCaptureError(error, "Get user fallback mount config");
				return null;
			}
		},
	},
});
